/**
 * @fileoverview Sales CRM My Page API service.
 * @description Calls the backend's My Page read endpoints (Confluence 3.5.11.15-.17): the caller's
 * targets (GET /sales/targets, every page at once), the KPI counts over the same window and scope
 * (GET /sales/targets/summary) and one target in full (GET /sales/targets/{targetId}), and its
 * write endpoints (.18-.23, .25): update stage, reopen, reassign, request a reassignment, send
 * payment details and confirm payment, plus the list of people a target may be reassigned to. The
 * backend does the scoping, filtering, sorting and every permission decision (a Super Admin sees
 * every target, anyone else only their own), so nothing here needs to know who the caller is. A
 * change to an existing target sends the `tg1.` version the user reviewed as a strong `If-Match`
 * entity tag, so a target that moved on since is refused (`412`) instead of overwritten. Separate
 * from targets.service.ts, whose per-tenant Target calls serve the mock Tenant/Target model the
 * other sales pages still read.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { readAllItems } from '@/features/sales/services/prospects.service'
import { httpClient } from '@/shared/api/http-client'

import type {
  MyPageEligibleReassignee,
  MyPagePaymentDetailsSent,
  MyPagePaymentRecipient,
  MyPageReassignInput,
  MyPageReassignmentRequestInput,
  MyPageReassignmentRequestResult,
  MyPageReopenInput,
  MyPageSummary,
  MyPageTargetDetails,
  MyPageTargetFilters,
  MyPageTargetRow,
  MyPageUpdateStageInput,
} from '@/features/sales/types/my-page.types'

/**
 * @description Turns the table's filters into the list endpoint's query: blank filters are left
 * out (the backend rejects a blank parameter), and `includeClosed` is only sent when it is false,
 * since true is the backend's default.
 * @param {MyPageTargetFilters} filters - The table's filters.
 * @returns {Record<string, string>} Only the parameters that have a value.
 */
function toListParams(filters: MyPageTargetFilters): Record<string, string> {
  const params: Record<string, string> = {}
  const search = filters.search?.trim()
  if (search) params.search = search
  if (filters.stage) params.stage = filters.stage
  if (filters.status) params.status = filters.status
  if (filters.priority) params.priority = filters.priority
  if (filters.includeClosed === false) params.includeClosed = 'false'
  return params
}

/**
 * @description Fetches every target the caller sees on My Page that matches the filters, page by
 * page, in the backend's order (due date, then priority, then bank name).
 * @param {MyPageTargetFilters} [filters] - Search, stage, status, priority and whether to include
 * closed targets.
 * @returns {Promise<MyPageTargetRow[]>} The matching targets.
 */
export function getAllMyPageTargets(filters: MyPageTargetFilters = {}): Promise<MyPageTargetRow[]> {
  return readAllItems<MyPageTargetRow>('/sales/targets', toListParams(filters))
}

/**
 * @description Fetches the My Page KPI counts, which ignore the table's filters.
 * @returns {Promise<MyPageSummary>} The financial-year window and the count under each display
 * status.
 */
export async function getMyPageSummary(): Promise<MyPageSummary> {
  const { data } = await httpClient.get<MyPageSummary>('/sales/targets/summary')
  return data
}

/**
 * @description Fetches one target in full: its row, the bank's contacts and its history.
 * @param {string} targetId - The target to fetch.
 * @returns {Promise<MyPageTargetDetails>} The target.
 */
export async function getMyPageTargetDetails(targetId: string): Promise<MyPageTargetDetails> {
  const { data } = await httpClient.get<MyPageTargetDetails>(
    `/sales/targets/${encodeURIComponent(targetId)}`,
  )
  return data
}

/**
 * @description Wraps a target version as the strong entity tag `If-Match` carries.
 * @param {string} version - The `tg1.` version from a target row.
 * @returns {string} The quoted entity tag.
 */
function entityTag(version: string): string {
  return `"${version}"`
}

/**
 * @description The path of one of a target's sub-resources.
 * @param {string} targetId - The target.
 * @param {string} suffix - The sub-path, e.g. `stage`.
 * @returns {string} The URL path.
 */
function targetPath(targetId: string, suffix: string): string {
  return `/sales/targets/${encodeURIComponent(targetId)}/${suffix}`
}

/**
 * @description Moves a target to another stage (Update Target Stage, 3.5.11.18).
 * @param {string} targetId - The target.
 * @param {string} version - The target's `tg1.` version the user reviewed.
 * @param {MyPageUpdateStageInput} input - The new stage and, for Lost, On Hold or Rejected, why.
 * @returns {Promise<MyPageTargetRow>} The target with its new version.
 */
export async function updateMyPageTargetStage(
  targetId: string,
  version: string,
  input: MyPageUpdateStageInput,
): Promise<MyPageTargetRow> {
  const { data } = await httpClient.post<MyPageTargetRow>(targetPath(targetId, 'stage'), input, {
    headers: { 'If-Match': entityTag(version) },
  })
  return data
}

/**
 * @description Reopens a target that is Awaiting Payment (Reopen Target, 3.5.11.19).
 * @param {string} targetId - The target.
 * @param {string} version - The target's `tg1.` version the user reviewed.
 * @param {MyPageReopenInput} input - The optional reason.
 * @returns {Promise<MyPageTargetRow>} The target with its new version.
 */
export async function reopenMyPageTarget(
  targetId: string,
  version: string,
  input: MyPageReopenInput,
): Promise<MyPageTargetRow> {
  const { data } = await httpClient.post<MyPageTargetRow>(targetPath(targetId, 'reopen'), input, {
    headers: { 'If-Match': entityTag(version) },
  })
  return data
}

/**
 * @description Lists everyone the caller may name as a target's new assignee (List Eligible Target
 * Reassignees, 3.5.11.20), every page at once.
 * @param {string} targetId - The target.
 * @returns {Promise<MyPageEligibleReassignee[]>} The people, ordered by name.
 */
export function getEligibleReassignees(targetId: string): Promise<MyPageEligibleReassignee[]> {
  return readAllItems<MyPageEligibleReassignee>(targetPath(targetId, 'eligible-reassignees'), {})
}

/**
 * @description Moves a target, with its prospect, to another user (Reassign Target, 3.5.11.21).
 * Needs a recent MFA sign-in (`403 / 2009` otherwise).
 * @param {string} targetId - The target.
 * @param {string} version - The target's `tg1.` version the user reviewed.
 * @param {MyPageReassignInput} input - The new assignee and an optional reason.
 * @returns {Promise<MyPageTargetRow>} The target with its new version.
 */
export async function reassignMyPageTarget(
  targetId: string,
  version: string,
  input: MyPageReassignInput,
): Promise<MyPageTargetRow> {
  const { data } = await httpClient.post<MyPageTargetRow>(targetPath(targetId, 'reassign'), input, {
    headers: { 'If-Match': entityTag(version) },
  })
  return data
}

/**
 * @description Asks for a target to be handed to a colleague (Request Target Reassignment,
 * 3.5.11.22): an Executive's route, since only a reviewer can approve the move. Sends no `If-Match`.
 * @param {string} targetId - The target.
 * @param {MyPageReassignmentRequestInput} input - The proposed assignee and the reason.
 * @returns {Promise<MyPageReassignmentRequestResult>} The approval request opened.
 */
export async function requestMyPageReassignment(
  targetId: string,
  input: MyPageReassignmentRequestInput,
): Promise<MyPageReassignmentRequestResult> {
  const { data } = await httpClient.post<MyPageReassignmentRequestResult>(
    targetPath(targetId, 'reassignment-requests'),
    input,
  )
  return data
}

/**
 * @description Emails the organisation's payment details to the bank's contact (Send Payment
 * Details, 3.5.11.23). Sends the `Idempotency-Key` it is given, so a retried click replays the
 * first request instead of emailing the bank twice; the mutation hook owns the key.
 * @param {string} targetId - The target.
 * @param {MyPagePaymentRecipient} recipient - Which bank contact to email.
 * @param {string} idempotencyKey - The command's key, kept by the caller across retries.
 * @returns {Promise<MyPagePaymentDetailsSent>} The target's payment state after the send.
 */
export async function sendMyPagePaymentDetails(
  targetId: string,
  recipient: MyPagePaymentRecipient,
  idempotencyKey: string,
): Promise<MyPagePaymentDetailsSent> {
  const { data } = await httpClient.post<MyPagePaymentDetailsSent>(
    targetPath(targetId, 'payment-details/send'),
    { recipient },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Confirms the bank's payment, completing the target (Confirm Payment, 3.5.11.24).
 * Super Admin only, needs a recent MFA sign-in, and cannot be undone.
 * @param {string} targetId - The target.
 * @param {string} version - The target's `tg1.` version the user reviewed.
 * @returns {Promise<MyPageTargetRow>} The completed target.
 */
export async function confirmMyPagePayment(
  targetId: string,
  version: string,
): Promise<MyPageTargetRow> {
  const { data } = await httpClient.post<MyPageTargetRow>(
    targetPath(targetId, 'payment/confirm'),
    {},
    { headers: { 'If-Match': entityTag(version) } },
  )
  return data
}
